import React, { useState } from 'react';
import { Settings, Trash2, AlertTriangle, Save, X } from 'lucide-react';
import * as api from '../services/api';

const AppManagement = ({ selectedApp, workerApiUrl, onAppDeleted, onAppUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [appName, setAppName] = useState(selectedApp.name);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (!appName.trim() || appName === selectedApp.name) {
      setIsEditing(false);
      setAppName(selectedApp.name);
      return;
    }

    setIsSaving(true);
    try {
      const updatedApp = await api.updateApp(workerApiUrl, selectedApp.id, { name: appName });
      setIsEditing(false);
      
      if (onAppUpdated) {
        onAppUpdated(updatedApp);
      }
    } catch (err) {
      alert(`Failed to update app: ${err.message}`);
      setAppName(selectedApp.name);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.deleteApp(workerApiUrl, selectedApp.id);
      setShowDeleteConfirm(false);
      
      if (onAppDeleted) {
        onAppDeleted(selectedApp.id);
      }
    } catch (err) {
      alert(`Failed to delete app: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="app-management">
      {/* App Details Section */}
      <section className="management-section">
        <div className="section-header">
          <Settings size={20} />
          <h2>Application Settings</h2>
        </div>

        <div className="settings-card">
          <div className="setting-item">
            <label>Application Name</label>
            <div className="setting-value">
              {isEditing ? (
                <div className="edit-name-container">
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    className="name-input"
                    autoFocus
                  />
                  <button 
                    className="btn-icon-primary" 
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <Save size={16} />
                  </button>
                  <button 
                    className="btn-icon-secondary" 
                    onClick={() => {
                      setIsEditing(false);
                      setAppName(selectedApp.name);
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="name-display">
                  <span className="app-name-text">{selectedApp.name}</span>
                  <button 
                    className="btn-secondary" 
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Name
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="setting-item">
            <label>Application ID</label>
            <div className="setting-value">
              <code className="app-id-code">{selectedApp.id}</code>
            </div>
          </div>

          <div className="setting-item">
            <label>Created At</label>
            <div className="setting-value">
              <span>{formatDate(selectedApp.created_at)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Danger Zone Section */}
      <section className="management-section danger-zone">
        <div className="section-header">
          <AlertTriangle size={20} />
          <h2>Danger Zone</h2>
        </div>

        <div className="danger-card">
          <div className="danger-content">
            <div>
              <h3>Delete Application</h3>
              <p>
                Once you delete an application, there is no going back. This will permanently delete
                all logs, tokens, and analytics data associated with this application.
              </p>
            </div>
            <button 
              className="btn-danger" 
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={16} />
              <span className="btn-text">Delete Application</span>
              
            </button>
          </div>
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
          <div className="modal danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <AlertTriangle size={24} color="#ef4444" />
              <h2>Delete Application</h2>
            </div>
            <div className="modal-body">
              <p>
                Are you absolutely sure you want to delete <strong>{selectedApp.name}</strong>?
              </p>
              <p className="warning-text">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="delete-list">
                <li>All logs and analytics data</li>
                <li>All API tokens</li>
                <li>All application settings</li>
              </ul>
              <div className="confirmation-input">
                <label>
                  Type <strong>{selectedApp.name}</strong> to confirm:
                </label>
                <input
                  type="text"
                  placeholder={selectedApp.name}
                  id="deleteConfirmInput"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger" 
                onClick={() => {
                  const input = document.getElementById('deleteConfirmInput');
                  if (input.value === selectedApp.name) {
                    handleDelete();
                  } else {
                    alert('Application name does not match');
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManagement;

